import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { api } from "@/api/client";
import { toastApiError } from "@/api/errors";
import { ErrorState } from "@/components/feedback/ErrorState";
import { LoadingState } from "@/components/feedback/LoadingState";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  type AgentDraft,
  draftFromAgent,
  emptyDraft,
  externalConfigFromApi,
  externalConfigToPut,
  ottoConfigFromApi,
  reportThemeFromApi,
  reportThemeToPut,
  type TabName,
  tabsForType,
} from "@/features/agents/components/AgentDraft";
import { BehaviorTab } from "@/features/agents/components/BehaviorTab";
import { ExternalConnectionTab } from "@/features/agents/components/ExternalConnectionTab";
import { IdentityTab } from "@/features/agents/components/IdentityTab";
import { KnowledgeTab } from "@/features/agents/components/KnowledgeTab";
import { ModelTab } from "@/features/agents/components/ModelTab";
import { OttoConfigTab } from "@/features/agents/components/OttoConfigTab";
import { PublicationTab } from "@/features/agents/components/PublicationTab";
import { RatingsTab } from "@/features/agents/components/RatingsTab";
import { ReportThemeTab } from "@/features/agents/components/ReportThemeTab";
import { SandboxTab } from "@/features/agents/components/SandboxTab";
import { ToolsTab } from "@/features/agents/components/ToolsTab";
import { VisibilityTab } from "@/features/agents/components/VisibilityTab";
import {
  useAgent,
  useAgentExternalConfig,
  useAgentKbs,
  useAgentMcps,
  useAgentOttoConfig,
  useAgentReportTheme,
  useAgentSkills,
  useAgentVisibility,
  useCreateAgent,
  useSetAgentKbs,
  useSetAgentMcps,
  useSetAgentSkills,
  useSetAgentVisibility,
  useUpdateAgent,
  useUpsertAgentExternalConfig,
  useUpsertAgentOttoConfig,
  useUpsertAgentReportTheme,
} from "@/features/agents/hooks/useAgents";

export function AgentEditorPage() {
  const { t } = useTranslation("agents");
  const { agentId } = useParams<{ agentId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isNew = !agentId || agentId === "new";

  // --- queries ---------------------------------------------------------
  const agentQuery = useAgent(isNew ? "" : agentId!);
  const kbsQuery = useAgentKbs(isNew ? "" : agentId!);
  const mcpsQuery = useAgentMcps(isNew ? "" : agentId!);
  const skillsQuery = useAgentSkills(isNew ? "" : agentId!);
  const visQuery = useAgentVisibility(isNew ? "" : agentId!);
  const ottoQuery = useAgentOttoConfig(isNew ? "" : agentId!);
  const themeQuery = useAgentReportTheme(isNew ? "" : agentId!);
  // So consulta a conexao externa quando o agente carregado e externo --
  // o GET exige permissao de edicao e nao se aplica a nativos.
  const isExternalAgent = agentQuery.data?.agent_type === "external";
  const externalQuery = useAgentExternalConfig(
    !isNew && isExternalAgent ? agentId! : "",
  );

  // --- mutations -------------------------------------------------------
  const createMut = useCreateAgent();
  const updateMut = useUpdateAgent(agentId ?? "");
  const setKbsMut = useSetAgentKbs(agentId ?? "");
  const setMcpsMut = useSetAgentMcps(agentId ?? "");
  const setSkillsMut = useSetAgentSkills(agentId ?? "");
  const setVisMut = useSetAgentVisibility(agentId ?? "");
  const upsertOttoMut = useUpsertAgentOttoConfig(agentId ?? "");
  const upsertThemeMut = useUpsertAgentReportTheme(agentId ?? "");
  const upsertExternalMut = useUpsertAgentExternalConfig(agentId ?? "");

  // --- draft state -----------------------------------------------------
  const [draft, setDraft] = useState<AgentDraft>(() => emptyDraft());
  const [original, setOriginal] = useState<AgentDraft>(() => emptyDraft());

  useEffect(() => {
    if (isNew) {
      const d = emptyDraft();
      setDraft(d);
      setOriginal(d);
      return;
    }
    if (
      agentQuery.data &&
      kbsQuery.data &&
      mcpsQuery.data &&
      skillsQuery.data &&
      visQuery.data &&
      ottoQuery.isSuccess &&
      themeQuery.isSuccess &&
      (agentQuery.data.agent_type !== "external" || externalQuery.isSuccess)
    ) {
      const d = draftFromAgent(agentQuery.data);
      d.kb_ids = kbsQuery.data.map((k) => k.knowledge_base_id);
      d.mcp_items = mcpsQuery.data.map((m) => ({
        mcp_server_id: m.mcp_server_id,
        enabled_tools: m.enabled_tools,
        tool_permissions: m.tool_permissions,
      }));
      d.skill_ids = skillsQuery.data.map((s) => s.skill_id);
      d.visibility_team_ids = visQuery.data.teams.map((t) => t.id);
      d.visibility_user_ids = visQuery.data.users.map((u) => u.id);
      // ottoQuery.data e null quando o agente nao tem config Otto.
      d.otto_config = ottoQuery.data
        ? ottoConfigFromApi(ottoQuery.data)
        : null;
      // themeQuery.data e null quando o agente nao tem tema configurado.
      d.report_theme = themeQuery.data
        ? reportThemeFromApi(themeQuery.data)
        : null;
      // externalQuery.data e null quando a conexao nao foi cadastrada.
      d.external_config = externalQuery.data
        ? externalConfigFromApi(externalQuery.data)
        : null;
      setDraft(d);
      setOriginal(d);
    }
  }, [
    isNew,
    agentQuery.data,
    kbsQuery.data,
    mcpsQuery.data,
    skillsQuery.data,
    visQuery.data,
    ottoQuery.isSuccess,
    ottoQuery.data,
    themeQuery.isSuccess,
    themeQuery.data,
    externalQuery.isSuccess,
    externalQuery.data,
  ]);

  // --- dirty tab tracking ---------------------------------------------
  const dirtyTabs = useMemo(() => {
    const dirty = new Set<TabName>();
    if (
      draft.name !== original.name ||
      draft.slug !== original.slug ||
      draft.description !== original.description ||
      draft.icon !== original.icon ||
      draft.owner_team_id !== original.owner_team_id
    )
      dirty.add("identity");
    if (
      draft.system_prompt !== original.system_prompt ||
      draft.response_tone !== original.response_tone ||
      draft.response_language !== original.response_language ||
      draft.fallback_behavior !== original.fallback_behavior ||
      draft.memory_enabled !== original.memory_enabled ||
      draft.cite_sources !== original.cite_sources ||
      draft.auto_escalate_low_confidence !== original.auto_escalate_low_confidence
    )
      dirty.add("behavior");
    if (
      draft.llm_model_id !== original.llm_model_id ||
      draft.reasoning_effort !== original.reasoning_effort ||
      draft.pii_filter_enabled !== original.pii_filter_enabled
    )
      dirty.add("model");
    if (
      JSON.stringify([...draft.kb_ids].sort()) !==
      JSON.stringify([...original.kb_ids].sort())
    )
      dirty.add("knowledge");
    if (
      JSON.stringify(draft.mcp_items) !== JSON.stringify(original.mcp_items) ||
      JSON.stringify([...draft.skill_ids].sort()) !==
        JSON.stringify([...original.skill_ids].sort()) ||
      draft.reports_enabled !== original.reports_enabled ||
      draft.web_search_enabled !== original.web_search_enabled
    )
      dirty.add("tools");
    if (
      draft.visibility !== original.visibility ||
      JSON.stringify([...draft.visibility_team_ids].sort()) !==
        JSON.stringify([...original.visibility_team_ids].sort()) ||
      JSON.stringify([...draft.visibility_user_ids].sort()) !==
        JSON.stringify([...original.visibility_user_ids].sort())
    )
      dirty.add("visibility");
    if (
      draft.requires_install_approval !== original.requires_install_approval ||
      draft.approval_responsible_user_id !==
        original.approval_responsible_user_id
    )
      dirty.add("publication");
    if (
      JSON.stringify(draft.otto_config) !== JSON.stringify(original.otto_config)
    )
      dirty.add("otto");
    if (
      JSON.stringify(draft.report_theme) !==
      JSON.stringify(original.report_theme)
    )
      dirty.add("report_theme");
    if (
      JSON.stringify(draft.external_config) !==
      JSON.stringify(original.external_config)
    )
      dirty.add("external");
    return dirty;
  }, [draft, original]);

  const isDirty = dirtyTabs.size > 0;

  // --- save -----------------------------------------------------------
  async function handleSave() {
    const isExternal = draft.agent_type === "external";
    // Validacao client-side da config Otto: habilitada exige prompts +
    // listas preenchidos (o backend tambem valida com 422). Agente
    // externo so precisa do toggle -- o bot remoto responde os fluxos.
    const otto = draft.otto_config;
    if (otto?.enabled && !isExternal) {
      const incomplete =
        !otto.funnel_prompt_text.trim() ||
        !otto.tags_prompt_text.trim() ||
        !otto.followup_prompt_text.trim() ||
        otto.funnel_steps.length === 0 ||
        otto.tag_options.length === 0;
      if (incomplete) {
        toast.error(t("otto.validationError"));
        return;
      }
    }
    // Conexao externa: se preenchida, base_url e obrigatoria.
    if (isExternal && draft.external_config) {
      if (!draft.external_config.base_url.trim()) {
        toast.error(t("external.baseUrlRequired"));
        return;
      }
    }

    if (isNew) {
      try {
        const created = await createMut.mutateAsync({
          name: draft.name,
          slug: draft.slug || undefined,
          agent_type: draft.agent_type,
          description: draft.description,
          icon: draft.icon ?? undefined,
          owner_team_id: draft.owner_team_id,
          // Agente externo nao tem prompt/LLM local.
          system_prompt: isExternal ? undefined : draft.system_prompt,
          llm_model_id: isExternal ? undefined : draft.llm_model_id,
          response_tone: draft.response_tone ?? undefined,
          response_language: draft.response_language,
          fallback_behavior: draft.fallback_behavior ?? undefined,
          memory_enabled: draft.memory_enabled,
          cite_sources: draft.cite_sources,
          auto_escalate_low_confidence: draft.auto_escalate_low_confidence,
          reasoning_effort: draft.reasoning_effort ?? undefined,
          pii_filter_enabled: draft.pii_filter_enabled,
          reports_enabled: isExternal ? false : draft.reports_enabled,
          web_search_enabled: isExternal ? false : draft.web_search_enabled,
          visibility: draft.visibility,
        });

        // Persistir KBs/MCPs/visibility selecionados no draft, ja que o
        // POST /agents nao aceita esses campos. As mutations bound via
        // useSetAgent* foram criadas com agentId="" antes do create —
        // por isso usamos `api.*` direto contra o id retornado.
        const followUps: Array<Promise<unknown>> = [];
        if (draft.kb_ids.length > 0) {
          followUps.push(
            api.PUT(`/agents/${created.id}/knowledge-bases`, {
              knowledge_base_ids: draft.kb_ids,
            }),
          );
        }
        if (draft.mcp_items.length > 0) {
          followUps.push(
            api.PUT(`/agents/${created.id}/mcps`, { items: draft.mcp_items }),
          );
        }
        if (draft.skill_ids.length > 0) {
          followUps.push(
            api.PUT(`/agents/${created.id}/skills`, {
              skill_ids: draft.skill_ids,
            }),
          );
        }
        if (
          draft.visibility_team_ids.length > 0 ||
          draft.visibility_user_ids.length > 0
        ) {
          followUps.push(
            api.PUT(`/agents/${created.id}/visibility`, {
              mode: draft.visibility,
              teams: draft.visibility_team_ids,
              users: draft.visibility_user_ids,
            }),
          );
        }
        if (draft.otto_config) {
          followUps.push(
            api.PUT(`/agents/${created.id}/otto-config`, draft.otto_config),
          );
        }
        if (draft.report_theme) {
          followUps.push(
            api.PUT(
              `/agents/${created.id}/report-theme`,
              reportThemeToPut(draft.report_theme),
            ),
          );
        }
        if (isExternal && draft.external_config?.base_url.trim()) {
          followUps.push(
            api.PUT(
              `/agents/${created.id}/external-config`,
              externalConfigToPut(draft.external_config),
            ),
          );
        }
        if (followUps.length > 0) {
          await Promise.all(followUps);
        }

        toast.success(t("toasts.created", { name: created.name }));
        navigate(`/me/agents/${created.id}?tab=identity`);
      } catch (err) {
        toastApiError(err);
      }
      return;
    }

    // Update existente: aplica diffs por aba.
    try {
      if (
        dirtyTabs.has("identity") ||
        dirtyTabs.has("behavior") ||
        dirtyTabs.has("model") ||
        dirtyTabs.has("publication") ||
        dirtyTabs.has("tools")
      ) {
        await updateMut.mutateAsync({
          name: dirtyTabs.has("identity") ? draft.name : undefined,
          slug: dirtyTabs.has("identity") ? draft.slug || undefined : undefined,
          description: dirtyTabs.has("identity") ? draft.description : undefined,
          icon: dirtyTabs.has("identity") ? draft.icon ?? undefined : undefined,
          system_prompt: dirtyTabs.has("behavior") ? draft.system_prompt : undefined,
          response_tone: dirtyTabs.has("behavior")
            ? draft.response_tone ?? undefined
            : undefined,
          response_language: dirtyTabs.has("behavior")
            ? draft.response_language
            : undefined,
          fallback_behavior: dirtyTabs.has("behavior")
            ? draft.fallback_behavior ?? undefined
            : undefined,
          memory_enabled: dirtyTabs.has("behavior")
            ? draft.memory_enabled
            : undefined,
          cite_sources: dirtyTabs.has("behavior")
            ? draft.cite_sources
            : undefined,
          auto_escalate_low_confidence: dirtyTabs.has("behavior")
            ? draft.auto_escalate_low_confidence
            : undefined,
          llm_model_id: dirtyTabs.has("model")
            ? draft.llm_model_id
            : undefined,
          reasoning_effort: dirtyTabs.has("model")
            ? draft.reasoning_effort
            : undefined,
          pii_filter_enabled: dirtyTabs.has("model")
            ? draft.pii_filter_enabled
            : undefined,
          reports_enabled: dirtyTabs.has("tools")
            ? draft.reports_enabled
            : undefined,
          web_search_enabled: dirtyTabs.has("tools")
            ? draft.web_search_enabled
            : undefined,
          requires_install_approval: dirtyTabs.has("publication")
            ? draft.requires_install_approval
            : undefined,
          approval_responsible_user_id: dirtyTabs.has("publication")
            ? draft.approval_responsible_user_id ?? undefined
            : undefined,
        });
      }
      if (dirtyTabs.has("knowledge")) {
        await setKbsMut.mutateAsync({ knowledge_base_ids: draft.kb_ids });
      }
      // tools abriga 3 controles (MCPs + 2 toggles built-in). Os toggles
      // ja foram propagados pelo updateMut acima; aqui chamamos PUT /mcps
      // somente se a lista de MCPs realmente mudou -- evita chamada
      // redundante ao backend quando o usuario so mexeu nos toggles.
      if (
        dirtyTabs.has("tools") &&
        JSON.stringify(draft.mcp_items) !== JSON.stringify(original.mcp_items)
      ) {
        await setMcpsMut.mutateAsync({ items: draft.mcp_items });
      }
      if (
        dirtyTabs.has("tools") &&
        JSON.stringify([...draft.skill_ids].sort()) !==
          JSON.stringify([...original.skill_ids].sort())
      ) {
        await setSkillsMut.mutateAsync({ skill_ids: draft.skill_ids });
      }
      if (dirtyTabs.has("visibility")) {
        await setVisMut.mutateAsync({
          mode: draft.visibility,
          teams: draft.visibility_team_ids,
          users: draft.visibility_user_ids,
        });
      }
      if (dirtyTabs.has("otto") && draft.otto_config) {
        await upsertOttoMut.mutateAsync(draft.otto_config);
      }
      if (dirtyTabs.has("report_theme") && draft.report_theme) {
        await upsertThemeMut.mutateAsync(reportThemeToPut(draft.report_theme));
      }
      if (dirtyTabs.has("external") && draft.external_config) {
        await upsertExternalMut.mutateAsync(
          externalConfigToPut(draft.external_config),
        );
      }
      toast.success(t("toasts.updated"));
      setOriginal(draft);
    } catch (err) {
      toastApiError(err);
    }
  }

  // --- tab navigation via ?tab= ---------------------------------------
  const currentTab = (searchParams.get("tab") as TabName) || "identity";
  function changeTab(tab: TabName) {
    setSearchParams({ tab });
  }

  // --- loading guards --------------------------------------------------
  if (!isNew && agentQuery.isLoading) return <LoadingState />;
  if (!isNew && agentQuery.isError)
    return <ErrorState error={agentQuery.error} />;

  const headerTitle = isNew
    ? t("identity.name")
    : draft.name || t("editor.untitled");
  const isPending =
    createMut.isPending ||
    updateMut.isPending ||
    setKbsMut.isPending ||
    setMcpsMut.isPending ||
    setVisMut.isPending ||
    upsertOttoMut.isPending ||
    upsertThemeMut.isPending ||
    upsertExternalMut.isPending;

  const visibleTabs = tabsForType(draft.agent_type);

  function switchAgentType(type: "native" | "external") {
    if (!isNew || draft.agent_type === type) return;
    setDraft({ ...draft, agent_type: type });
    // Aba atual pode nao existir no novo tipo -> volta para identidade.
    if (!tabsForType(type).includes(currentTab)) {
      changeTab("identity");
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        backTo="/me/agents"
        title={isNew ? t("actions.new") : headerTitle}
        description={
          !isNew ? (
            <span className="font-mono text-xs text-ink-500">{draft.slug}</span>
          ) : undefined
        }
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => navigate("/me/agents")}
              disabled={isPending}
            >
              {t("actions.cancel")}
            </Button>
            <Button onClick={handleSave} disabled={isPending || !isDirty}>
              {t("actions.save")}
            </Button>
          </>
        }
      />

      {isNew ? (
        <div className="flex items-center gap-3 rounded-md border p-4">
          <span className="text-sm font-medium">
            {t("external.typeSelectorLabel")}
          </span>
          <div className="inline-flex overflow-hidden rounded-md border">
            <button
              type="button"
              onClick={() => switchAgentType("native")}
              className={
                draft.agent_type === "native"
                  ? "bg-primary px-3 py-1.5 text-sm text-primary-foreground"
                  : "px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
              }
            >
              {t("external.typeNative")}
            </button>
            <button
              type="button"
              onClick={() => switchAgentType("external")}
              className={
                draft.agent_type === "external"
                  ? "bg-primary px-3 py-1.5 text-sm text-primary-foreground"
                  : "px-3 py-1.5 text-sm text-muted-foreground hover:bg-muted"
              }
            >
              {t("external.typeExternal")}
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            {draft.agent_type === "external"
              ? t("external.typeExternalHint")
              : t("external.typeNativeHint")}
          </p>
        </div>
      ) : null}

      <Tabs value={currentTab} onValueChange={(v) => changeTab(v as TabName)}>
        <TabsList className="flex-wrap">
          {visibleTabs.map((tab) => (
            <TabsTrigger key={tab} value={tab}>
              {t(`tabs.${tab}`)}
              {dirtyTabs.has(tab) ? (
                <span className="ml-1 inline-block h-2 w-2 rounded-full bg-brand-500" />
              ) : null}
            </TabsTrigger>
          ))}
          <TabsTrigger value="testing">{t("tabs.testing")}</TabsTrigger>
          {!isNew ? (
            <TabsTrigger value="ratings">{t("tabs.ratings")}</TabsTrigger>
          ) : null}
        </TabsList>

        <TabsContent value="identity" className="space-y-4">
          <IdentityTab
            draft={draft}
            onChange={setDraft}
            isNew={isNew}
          />
        </TabsContent>

        <TabsContent value="behavior" className="space-y-4">
          <BehaviorTab
            draft={draft}
            onChange={setDraft}
            agentId={isNew ? undefined : agentId}
          />
        </TabsContent>

        <TabsContent value="model" className="space-y-4">
          <ModelTab draft={draft} onChange={setDraft} />
        </TabsContent>

        <TabsContent value="knowledge" className="space-y-4">
          <KnowledgeTab draft={draft} onChange={setDraft} />
        </TabsContent>

        <TabsContent value="tools" className="space-y-4">
          <ToolsTab draft={draft} onChange={setDraft} />
        </TabsContent>

        <TabsContent value="visibility" className="space-y-4">
          <VisibilityTab draft={draft} onChange={setDraft} />
        </TabsContent>

        <TabsContent value="publication" className="space-y-4">
          <PublicationTab
            draft={draft}
            onChange={setDraft}
            agent={agentQuery.data ?? undefined}
            agentId={isNew ? undefined : agentId}
          />
        </TabsContent>

        <TabsContent value="otto" className="space-y-4">
          <OttoConfigTab draft={draft} onChange={setDraft} />
        </TabsContent>

        <TabsContent value="report_theme" className="space-y-4">
          <ReportThemeTab draft={draft} onChange={setDraft} />
        </TabsContent>

        <TabsContent value="external" className="space-y-4">
          <ExternalConnectionTab
            draft={draft}
            onChange={setDraft}
            agentId={isNew ? undefined : agentId}
            isDirty={dirtyTabs.has("external")}
          />
        </TabsContent>

        <TabsContent value="testing" className="space-y-4">
          <SandboxTab
            agentId={!isNew && agentId ? agentId : ""}
            disabled={isNew || dirtyTabs.size > 0}
          />
        </TabsContent>

        {!isNew && agentId ? (
          <TabsContent value="ratings" className="space-y-4">
            <RatingsTab agentId={agentId} />
          </TabsContent>
        ) : null}
      </Tabs>
    </div>
  );
}
